import React from 'react';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SaveOutlined, 
  SearchOutlined,
  DownloadOutlined,
  UploadOutlined,
  SendOutlined,
  CheckOutlined,
  CloseOutlined,
  SettingOutlined,
  EyeOutlined
} from '@ant-design/icons';
import { BusinessButton, BusinessButtonProps } from '../BusinessButton/BusinessButton';

// Extend the props interface
interface ExtendedBusinessButtonProps extends Omit<BusinessButtonProps, 'children'> {
  children?: React.ReactNode;
}

// Primary Action Buttons
export const CreateButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Create', ...props }) => (
  <BusinessButton 
    variant="primary" 
    icon={<PlusOutlined />} 
    gradient={true}
    elevated={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const SaveButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Save', ...props }) => (
  <BusinessButton 
    variant="success" 
    icon={<SaveOutlined />} 
    gradient={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const SubmitButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Submit', ...props }) => (
  <BusinessButton 
    variant="primary" 
    icon={<SendOutlined />} 
    gradient={true}
    businessSize="large"
    {...props}
  >
    {children}
  </BusinessButton>
);

// Secondary Action Buttons
export const EditButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Edit', ...props }) => (
  <BusinessButton 
    variant="secondary" 
    icon={<EditOutlined />} 
    gradient={false}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const ViewButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'View', ...props }) => (
  <BusinessButton 
    variant="ghost" 
    icon={<EyeOutlined />} 
    {...props}
  >
    {children}
  </BusinessButton>
);

export const SearchButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Search', ...props }) => (
  <BusinessButton 
    variant="secondary" 
    icon={<SearchOutlined />} 
    rounded={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

// Danger Actions
export const DeleteButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Delete', ...props }) => (
  <BusinessButton 
    variant="danger" 
    icon={<DeleteOutlined />} 
    gradient={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const CancelButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Cancel', ...props }) => (
  <BusinessButton 
    variant="text" 
    icon={<CloseOutlined />} 
    {...props}
  >
    {children}
  </BusinessButton>
);

// File Actions
export const DownloadButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Download', ...props }) => (
  <BusinessButton 
    variant="secondary" 
    icon={<DownloadOutlined />} 
    elevated={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const UploadButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Upload', ...props }) => (
  <BusinessButton 
    variant="primary" 
    icon={<UploadOutlined />} 
    gradient={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

// Status Buttons
export const ApproveButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Approve', ...props }) => (
  <BusinessButton 
    variant="success" 
    icon={<CheckOutlined />} 
    gradient={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

export const RejectButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Reject', ...props }) => (
  <BusinessButton 
    variant="danger" 
    icon={<CloseOutlined />} 
    gradient={false}
    {...props}
  >
    {children}
  </BusinessButton>
);

// Settings Button
export const SettingsButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Settings', ...props }) => (
  <BusinessButton 
    variant="secondary" 
    icon={<SettingOutlined />} 
    rounded={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

// Warning Button
export const WarningButton: React.FC<ExtendedBusinessButtonProps> = ({ children = 'Warning', ...props }) => (
  <BusinessButton 
    variant="warning" 
    gradient={true}
    elevated={true}
    {...props}
  >
    {children}
  </BusinessButton>
);

// Compact Icon Buttons (без текста)
export const CompactCreateButton: React.FC<Omit<ExtendedBusinessButtonProps, 'children'>> = (props) => (
  <BusinessButton 
    variant="primary" 
    icon={<PlusOutlined />} 
    gradient={true}
    businessSize="small"
    rounded={true}
    {...props}
  />
);

export const CompactEditButton: React.FC<Omit<ExtendedBusinessButtonProps, 'children'>> = (props) => (
  <BusinessButton 
    variant="secondary" 
    icon={<EditOutlined />} 
    businessSize="small"
    rounded={true}
    {...props}
  />
);

export const CompactDeleteButton: React.FC<Omit<ExtendedBusinessButtonProps, 'children'>> = (props) => (
  <BusinessButton 
    variant="danger" 
    icon={<DeleteOutlined />} 
    businessSize="small"
    rounded={true}
    {...props}
  />
);
